/**
 * SegmentedControl — a small, always-visible set of mutually exclusive views.
 *
 * The selected state is a single travelling thumb measured off the live DOM, so
 * it stays exact when labels change width or the font finishes loading. Semantics
 * are a radio group with a roving tabindex: one tab stop, arrows to move.
 */

import { forwardRef, useCallback, useLayoutEffect, useRef, useState } from 'react';
import type { KeyboardEvent, ReactNode } from 'react';
import { cn } from '@/lib/cn';
import { composeRefs } from './Button';
import type { ControlSize } from './Button';

export interface SegmentedOption {
  value: string;
  label?: ReactNode;
  icon?: ReactNode;
  count?: number;
  disabled?: boolean;
  /** Required when the option renders as an icon only. */
  'aria-label'?: string;
}

/**
 * `sm` insets by 1px rather than 2 so its radio still measures 24px tall — the
 * WCAG 2.2 SC 2.5.8 floor — inside the same 26px shell.
 */
const SHELL: Record<ControlSize, string> = {
  sm: 'h-[var(--h-control-sm)] p-px',
  md: 'h-[var(--h-control)] p-0.5',
  lg: 'h-[var(--h-control-lg)] p-0.5',
};

const ITEM: Record<ControlSize, string> = {
  sm: 'h-[calc(var(--h-control-sm)-2px)] min-w-6 gap-1 px-1.5 text-sm',
  md: 'h-[calc(var(--h-control)-4px)] gap-1.5 px-2 text-base',
  lg: 'h-[calc(var(--h-control-lg)-4px)] gap-1.5 px-2.5 text-base',
};

/** The travelling thumb tracks the shell's own inset. */
const THUMB_INSET: Record<ControlSize, string> = {
  sm: 'inset-y-px',
  md: 'inset-y-0.5',
  lg: 'inset-y-0.5',
};

export interface SegmentedControlProps {
  options: SegmentedOption[];
  value: string;
  onValueChange?: (value: string) => void;
  size?: ControlSize;
  fullWidth?: boolean;
  disabled?: boolean;
  className?: string;
  'aria-label'?: string;
  'aria-labelledby'?: string;
}

export const SegmentedControl = forwardRef<HTMLDivElement, SegmentedControlProps>(
  function SegmentedControl(
    {
      options,
      value,
      onValueChange,
      size = 'md',
      fullWidth = false,
      disabled = false,
      className,
      ...aria
    },
    ref,
  ) {
    const containerRef = useRef<HTMLDivElement>(null);
    const itemRefs = useRef<Array<HTMLButtonElement | null>>([]);
    const [thumb, setThumb] = useState<{ left: number; width: number } | null>(null);
    /** Suppresses the travel animation on the very first paint. */
    const [primed, setPrimed] = useState(false);

    const selectedIndex = options.findIndex((option) => option.value === value);

    useLayoutEffect(() => {
      const container = containerRef.current;
      const active = selectedIndex >= 0 ? itemRefs.current[selectedIndex] : null;
      if (!container || !active) {
        setThumb(null);
        return;
      }

      const measure = () => {
        const outer = container.getBoundingClientRect();
        const inner = active.getBoundingClientRect();
        const borderLeft = parseFloat(getComputedStyle(container).borderLeftWidth) || 0;
        setThumb({ left: inner.left - outer.left - borderLeft, width: inner.width });
      };

      measure();
      const frame = requestAnimationFrame(() => setPrimed(true));
      const observer = new ResizeObserver(measure);
      observer.observe(container);
      observer.observe(active);
      return () => {
        cancelAnimationFrame(frame);
        observer.disconnect();
      };
    }, [options, selectedIndex]);

    const focusIndex = useCallback((index: number) => {
      const node = itemRefs.current[index];
      node?.focus();
    }, []);

    const step = useCallback(
      (from: number, delta: number) => {
        const count = options.length;
        for (let hop = 1; hop <= count; hop += 1) {
          const next = (from + delta * hop + count * count) % count;
          const option = options[next];
          if (option && !option.disabled) {
            onValueChange?.(option.value);
            focusIndex(next);
            return;
          }
        }
      },
      [focusIndex, onValueChange, options],
    );

    const handleKeyDown = (event: KeyboardEvent<HTMLButtonElement>, index: number) => {
      switch (event.key) {
        case 'ArrowRight':
        case 'ArrowDown':
          event.preventDefault();
          step(index, 1);
          break;
        case 'ArrowLeft':
        case 'ArrowUp':
          event.preventDefault();
          step(index, -1);
          break;
        case 'Home':
          event.preventDefault();
          step(-1, 1);
          break;
        case 'End':
          event.preventDefault();
          step(options.length, -1);
          break;
        default:
          break;
      }
    };

    const tabStop = selectedIndex >= 0 ? selectedIndex : options.findIndex((o) => !o.disabled);

    return (
      <div
        ref={composeRefs(ref, containerRef)}
        role="radiogroup"
        aria-disabled={disabled || undefined}
        className={cn(
          'relative isolate inline-flex items-center rounded-sm border border-line-subtle bg-surface-inset',
          SHELL[size],
          fullWidth && 'flex w-full',
          disabled && 'opacity-70',
          className,
        )}
        {...aria}
      >
        {thumb ? (
          <span
            aria-hidden="true"
            className={cn(
              'absolute left-0 -z-10 rounded-xs border border-line-subtle bg-surface-1 shadow-1',
              THUMB_INSET[size],
              primed && 'transition-[transform,width] duration-[var(--dur-normal)] ease-[var(--ease-out)]',
            )}
            style={{ width: thumb.width, transform: `translateX(${thumb.left}px)` }}
          />
        ) : null}

        {options.map((option, index) => {
          const selected = option.value === value;
          const itemDisabled = disabled || option.disabled === true;

          return (
            <button
              key={option.value}
              ref={(node) => {
                itemRefs.current[index] = node;
              }}
              type="button"
              role="radio"
              aria-checked={selected}
              aria-label={option['aria-label']}
              disabled={itemDisabled}
              tabIndex={index === tabStop ? 0 : -1}
              onClick={() => onValueChange?.(option.value)}
              onKeyDown={(event) => handleKeyDown(event, index)}
              className={cn(
                'relative inline-flex shrink-0 items-center justify-center rounded-xs! font-medium whitespace-nowrap',
                // Unconditional, because `transition-colors` includes
                // `outline-color`: a ring colour that only exists under
                // `:focus-visible` gets interpolated from `currentColor`, so the
                // segment painted a `--text-primary` ring for the first 140ms.
                'transition-colors duration-[var(--dur-fast)] ease-[var(--ease-out)]',
                'outline-[color:var(--focus)]',
                '[&_svg]:size-3.5 [&_svg]:shrink-0 [&_svg]:-translate-y-[0.5px]',
                ITEM[size],
                fullWidth && 'flex-1',
                // Unselected labels are 12px on `--surface-inset`; `--text-muted`
                // measures 3.98:1 there in the light theme, so they sit one step
                // up the ramp and the thumb carries the selected state instead.
                selected ? 'text-primary' : 'text-secondary',
                !selected && !itemDisabled && 'hover:text-primary',
                itemDisabled && 'cursor-not-allowed text-muted',
              )}
            >
              {option.icon}
              {option.label}
              {option.count !== undefined ? (
                <span className="ml-0.5 text-muted tnum">{option.count}</span>
              ) : null}
            </button>
          );
        })}
      </div>
    );
  },
);
